import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Building2, Mail, Key, Loader2, HardHat, Shield, FileCheck } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

const SubcontractorLogin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: searchParams.get("email") || "",
    invite_token: searchParams.get("token") || ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.invite_token) {
      toast.error("Please enter email and invite code");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE}/api/portal/login`, formData);
      
      // Store session token
      localStorage.setItem("portal_token", res.data.session_token);
      localStorage.setItem("portal_sub", JSON.stringify(res.data.subcontractor));
      
      toast.success("Login successful!");
      navigate("/subcontractor-portal/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed. Check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <Toaster position="top-right" richColors />
      
      <div className="w-full max-w-md">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-500/20 rounded-2xl mb-4">
            <HardHat className="h-8 w-8 text-orange-400" />
          </div>
          <h1 className="text-3xl font-bold text-white">Subcontractor Portal</h1>
          <p className="text-slate-400 mt-2">Sign in to access your dashboard</p>
        </div>

        {/* Login Card */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl text-white text-center">Sign In</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-300">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="your@email.com"
                    className="pl-10 bg-slate-700 border-slate-600 text-white placeholder:text-slate-500 h-12"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-300">Invite Code</label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
                  <Input
                    type="text"
                    value={formData.invite_token}
                    onChange={(e) => setFormData({ ...formData, invite_token: e.target.value })}
                    placeholder="Enter your invite code"
                    className="pl-10 bg-slate-700 border-slate-600 text-white placeholder:text-slate-500 h-12"
                  />
                </div>
                <p className="text-xs text-slate-500">
                  Check your email invitation for this code
                </p>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white h-12 text-base font-semibold"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  "Sign In"
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Features */}
        <div className="mt-8 grid grid-cols-3 gap-4 text-center">
          <div className="p-4">
            <FileCheck className="h-6 w-6 text-orange-400 mx-auto mb-2" />
            <p className="text-xs text-slate-400">View Job Offers</p>
          </div>
          <div className="p-4">
            <Shield className="h-6 w-6 text-orange-400 mx-auto mb-2" />
            <p className="text-xs text-slate-400">Upload Documents</p>
          </div>
          <div className="p-4">
            <Building2 className="h-6 w-6 text-orange-400 mx-auto mb-2" />
            <p className="text-xs text-slate-400">Manage Profile</p>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-slate-500 text-sm mt-8">
          Need help? Contact your general contractor.
        </p>
      </div>
    </div>
  );
};

export default SubcontractorLogin;
